/**
 * Deferred, serialised autoplay video.
 *
 * Autoplay behaviour is preserved — every video still starts on its own, no tap
 * required. What changes is WHEN each one is allowed to begin downloading.
 *
 * Why: the PDP feature-highlight videos are 4.5–8.3 MB each (~21 MB per section).
 * Left alone the browser starts all of them at once, so on a mobile connection
 * they all crawl and nothing appears for 10+ seconds. Loading them one at a time
 * gives the video the customer is actually looking at the whole pipe, so it
 * appears in a few seconds instead of all three stalling together.
 *
 * Rules:
 *   - sources are detached up front so nothing can fetch before we allow it
 *   - a queue admits 1 video at a time on mobile, 2 on desktop
 *   - the nearest-to-viewport video is always admitted first
 *   - a video releases its slot once it can actually play, or after a timeout
 *   - offscreen videos pause, freeing bandwidth and CPU for what is on screen
 *   - on Save-Data or 2g/3g nothing is prefetched ahead of the viewport
 *
 * No markup changes: it works off `video[autoplay]`.
 */
(function () {
  if (!('IntersectionObserver' in window)) return; // leave native behaviour alone

  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
  var slowNetwork = !!conn.saveData || /^(slow-)?2g$|^3g$/.test(conn.effectiveType || '');
  var isMobile = window.matchMedia('(max-width: 767px)').matches;

  // One at a time on mobile — two videos sharing a mobile pipe is what makes both slow.
  var MAX_CONCURRENT = isMobile || slowNetwork ? 1 : 2;
  // Don't prefetch ahead on mobile/slow links; the on-screen video needs the bandwidth.
  var ROOT_MARGIN = isMobile || slowNetwork ? '0px' : '300px 0px';
  // Don't let one stalled video hold the queue shut forever.
  var SLOT_TIMEOUT = 8000;

  var queue = [];
  var active = 0;

  function detach(video) {
    var sources = [];
    if (video.getAttribute('src')) {
      sources.push([video, video.getAttribute('src')]);
      video.removeAttribute('src');
    }
    var tags = video.querySelectorAll('source[src]');
    for (var i = 0; i < tags.length; i++) {
      sources.push([tags[i], tags[i].getAttribute('src')]);
      tags[i].removeAttribute('src');
    }
    video._sources = sources;
    video.removeAttribute('autoplay'); // we call play() ourselves, at the right moment
    video.setAttribute('preload', 'none');
    video.muted = true;                // muted is required for programmatic autoplay
    video.setAttribute('playsinline', '');
    if (!video.getAttribute('poster') && !video.style.backgroundColor) {
      video.style.backgroundColor = '#f2f4f6'; // hold the box so nothing shifts
    }
    try { video.load(); } catch (e) {}
  }

  function distance(video) {
    var r = video.getBoundingClientRect();
    if (r.bottom > 0 && r.top < window.innerHeight) return 0; // on screen
    return r.top > 0 ? r.top - window.innerHeight : -r.bottom;
  }

  function release(video) {
    if (video._released) return;
    video._released = true;
    active--;
    pump();
  }

  function start(video) {
    active++;
    video._started = true;
    video._released = false;

    var done = function () { release(video); };
    // Free the slot as soon as it has enough to play, not when fully downloaded.
    video.addEventListener('loadeddata', done, { once: true });
    video.addEventListener('canplay', done, { once: true });
    video.addEventListener('error', done, { once: true });
    setTimeout(done, SLOT_TIMEOUT);

    var sources = video._sources || [];
    for (var i = 0; i < sources.length; i++) {
      sources[i][0].setAttribute('src', sources[i][1]);
    }
    try { video.load(); } catch (e) {}
    play(video);
  }

  function play(video) {
    video.muted = true;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function pump() {
    while (active < MAX_CONCURRENT && queue.length) {
      // Always serve whatever is closest to the viewport first.
      queue.sort(function (a, b) { return distance(a) - distance(b); });
      var next = queue.shift();
      if (next && !next._started) start(next);
    }
  }

  function enqueue(video) {
    if (video._started || queue.indexOf(video) !== -1) return;
    queue.push(video);
    pump();
  }

  function init() {
    var videos = document.querySelectorAll('video[autoplay]');
    if (!videos.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            if (video._started) play(video); // already loaded — just resume
            else enqueue(video);
          } else {
            // Offscreen: stop spending bandwidth and CPU on it.
            var i = queue.indexOf(video);
            if (i !== -1) queue.splice(i, 1);
            if (video._started && !video.paused) video.pause();
          }
        });
      },
      { rootMargin: ROOT_MARGIN, threshold: 0 }
    );

    for (var i = 0; i < videos.length; i++) {
      detach(videos[i]);
      observer.observe(videos[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
